import { omit } from "lodash";

import {
  formatValidQueryParamUrl,
  formatValidQueryParamUrlCommaSeparated
} from "utils/common";
import { formatProductCatalogOption } from "utils/products.utils";
import { viteEnv } from "utils/vite-env.util";

import { createPaginatedEndpoint } from "../utils/services.util";
import { emptySplitApi } from "./empty-split-api";
import {
  Product,
  ProductBulkAssignCPTCodesInput,
  ProductBulkAssignCPTCodesResponse,
  ProductCSVQuery,
  ProductCatalogOption,
  ProductInput,
  ProductsFilters,
  ProductsResponse
} from "./types/products.type";
import { CSVReportResponse } from "./types/reports.type";

export const DEFAULT_PRODUCTS_LIMIT = 25;

// RestQL selector for the optional Product.cpt_codes M2M field.
// Gated behind multipleCptCodesFeature to avoid querying a field the BE may
// not expose yet (deploy-ordering safety).
const cptCodesSelector = (multipleCptCodesFeature?: boolean): string =>
  multipleCptCodesFeature ? ",cpt_codes{id,code,description}" : "";

export const getProductListQuery = (
  multipleCptCodesFeature?: boolean
): string => {
  const cptCodes = cptCodesSelector(multipleCptCodesFeature);
  return `{id,name,display_name,starred,repair_eligible,specification,auto_delivery,master_product{id,cpt_code{id,code,description}${cptCodes},type,subtype,description,specification},price,is_active,managed_stock,supplier_price,max_discount,cpt_code{id,code,description}${cptCodes},type,taxable,income_account,expense_account}`;
};

export type CatalogCounts = {
  all: number;
  services: number;
  hearing_aids: number;
  accessories: number;
  fees: number;
  supplies: number;
  inactive: number;
};

export const productsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getProduct: builder.query<
      Product,
      { id: number; multipleCptCodesFeature?: boolean }
    >({
      query: ({ id, multipleCptCodesFeature }) => {
        const cptCodes = cptCodesSelector(multipleCptCodesFeature);
        return `/products/${id}/?query={*,cpt_code{id,code,description}${cptCodes},master_product{*,cpt_code{id,code,description}${cptCodes}}}`;
      },
      providesTags: ["Products"]
    }),
    getProducts: builder.query<Product[], ProductsFilters>({
      query: ({ query = "{*}", limit = 300, search, ...filterParams }) =>
        `/products/?limit=${limit}&${formatValidQueryParamUrlCommaSeparated({
          ...filterParams,
          search: search ? encodeURIComponent(search) : undefined
        })}&query=${query}`,
      transformResponse: (response: ProductsResponse) => {
        return response.results;
      },
      providesTags: ["Products"]
    }),
    getProductsCount: builder.query<number, ProductsFilters>({
      query: ({ query = "{id}", limit, offset, ...filterParams }) =>
        `/products/?limit=0&offset=0&${formatValidQueryParamUrlCommaSeparated(
          filterParams
        )}&query=${query}`,
      transformResponse: (response: { count: number }) => {
        return response.count || 0;
      },
      providesTags: ["Products"]
    }),
    getProductsPaginated: createPaginatedEndpoint<ProductsFilters, Product[]>(
      builder,
      {
        queryFn: ({ query = "{*}", limit = 300, search, ...filterParams }) =>
          `/products/?limit=${limit}&${formatValidQueryParamUrlCommaSeparated({
            ...filterParams,
            search: search && encodeURIComponent(search)
          })}&query=${query}`,
        transformResponseFn: (response: ProductsResponse) => response.results
      }
    ),
    getProductCatalogOptions: builder.query<
      ProductCatalogOption[],
      ProductsFilters
    >({
      query: ({ query = "{*}", limit = 300, search, ...filterParams }) =>
        `/products/?limit=${limit}&${formatValidQueryParamUrlCommaSeparated({
          ...filterParams,
          search: search && encodeURIComponent(search)
        })}&query=${query}`,
      transformResponse: (response: ProductsResponse) => {
        return formatProductCatalogOption(response.results);
      },
      providesTags: ["Products"]
    }),
    queryProductsCounter: builder.query<
      number,
      Omit<ProductsFilters, "query" | "limit" | "offset">
    >({
      query: (filters) =>
        `/products/count/?${formatValidQueryParamUrlCommaSeparated({
          ...filters
        })}`,
      providesTags: ["Products"]
    }),
    getCatalogCounts: builder.query<
      CatalogCounts,
      { include_migrated?: boolean }
    >({
      query: ({ include_migrated }) =>
        `/products/counts/?${formatValidQueryParamUrlCommaSeparated({
          include_migrated
        })}`,
      providesTags: ["Products"]
    }),
    addProduct: builder.mutation<Product, ProductInput>({
      query: (product) => ({
        url: "/products/",
        method: "POST",
        body: product
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    editProduct: builder.mutation<Product, ProductInput>({
      query: (product) => ({
        url: `/products/${product.id}/`,
        method: "PATCH",
        body: omit(product, "id")
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    bulkAssignProductCPTCodes: builder.mutation<
      ProductBulkAssignCPTCodesResponse,
      ProductBulkAssignCPTCodesInput
    >({
      query: ({ filters, primary_cpt_code, secondary_cpt_code }) => {
        const filterParams = omit(filters, ["query", "limit", "offset"]);
        return {
          url: `/products/bulk-assign-cpt-codes/?${formatValidQueryParamUrlCommaSeparated(
            {
              ...filterParams,
              search: filters.search && encodeURIComponent(filters.search)
            }
          )}`,
          method: "POST",
          body: { primary_cpt_code, secondary_cpt_code }
        };
      },
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    addProductToClinicBulk: builder.mutation<
      Product,
      {
        clinic_ids: number[];
        product_ids: number[];
      }
    >({
      query: (body) => ({
        url: "/products-bulk-add/",
        method: "POST",
        body: body
      }),
      invalidatesTags: ["Products"]
    }),
    updateProductClinics: builder.mutation<
      Product,
      {
        productId: number;
        clinics: {
          add?: number[];
          remove?: number[];
        };
      }
    >({
      query: ({ productId, ...body }) => ({
        url: `/products/${productId}/`,
        method: "PATCH",
        body: body
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),

    downloadProductsCSV: builder.query<CSVReportResponse, ProductCSVQuery>({
      query: (filters) => ({
        url: `${
          viteEnv.VITE_SUNO_API
        }/products/?${formatValidQueryParamUrl({ ...filters, search: filters.search && encodeURIComponent(filters.search) })}`,
        headers: { Accept: "custom-csv/json" }
      })
    })
  })
});

export const {
  useGetProductsQuery,
  useGetProductsCountQuery,
  useGetProductQuery,
  useGetProductCatalogOptionsQuery,
  useAddProductMutation,
  useEditProductMutation,
  useBulkAssignProductCPTCodesMutation,
  useQueryProductsCounterQuery,
  useLazyQueryProductsCounterQuery,
  useGetCatalogCountsQuery,
  useAddProductToClinicBulkMutation,
  useUpdateProductClinicsMutation,
  useLazyDownloadProductsCSVQuery
} = productsApi;
